/**
 * The SearchBox view allows to create search control on a map.
 *
 * The tomtom.searchBox() method creates a new instance of SearchBox component.
 *
 * @module Widgets
 * @class searchBox
 *
 */
define('widgets/controls/searchBox',['display/map', 'services/search/fuzzySearch', 'services/reverseGeocode/reverseGeocode',
    'widgets/controls/location', 'services/utils', 'unitFormatConverter/unitFormatConverter'],
    function(L, searchService, reverseGeocodeService, location, utils, unitFormatConverter) {
        'use strict';

        var SearchBox;
        var ENTER = 13, ARROWUP = 38, ARROWDOWN = 40,
            EVENTS = {
                ResultCreated: 'resultCreated',
                ResultClicked: 'resultClicked',
                ClearButtonClicked: 'clearButtonClicked',
                ResultsCleared: 'resultsCleared',
                ResultsFound: 'resultsFound',
                ResultsDrawn: 'resultsDrawn',
                SearchInputChanged: 'searchInputChanged',
                LocationError: 'locationError'
            };

        function searchBoxEvent(element, data, text) {
            return { element: element, data: data, text: text };
        }

        function locationError(control) {
            return function(error) {
                control.input.value = '';
                control.input.focus();
                control.clearResultsContainer();
                createNoResults(control);
                control.fire(EVENTS.LocationError, searchBoxEvent(control.input, error, error.message));
                console.error(error);
            };
        }

        function locationSuccess(control) {
            return function(location) {
                var options = utils.clone(control.options.serviceOptions);
                reverseGeocodeService(options).position(location.latlng).go(function(result) {
                    result.position = location.latlng;
                    control.clearResultsContainer();
                    control.input.value = resolveResultText(result);
                    control.input.focus();
                    control.fire(EVENTS.ResultClicked, searchBoxEvent(control.locationIcon, result, control.input.value));
                });
            };
        }

        function createCurrentLocationButton(control) {
            control.location = location({ size: null });
            control.locationIcon = control.location.onAdd(control._map);
            control.inputContainer.appendChild(control.locationIcon);
            control.location.on(control.location.Event.LocationFound, locationSuccess(control));
            control.location.on(control.location.Event.LocationError, locationError(control));
        }

        function createDOMElements(control) {
            var container = new L.DomUtil.create('div', 'leaflet-control-search');
            control._container = container;
            control.inputContainer = new L.DomUtil.create('div', 'leaflet-control-search-input-container', container);
            control.input = new L.DomUtil.create('input', control.options.icon + ' leaflet-control-search-input', control.inputContainer);
            control.input.required = true;
            control.clearButton = new L.DomUtil.create('div', 'leaflet-control-search-input-close-icon icon-close-gray', control.inputContainer);
            control.resultsContainer = new L.DomUtil.create('div', 'leaflet-control-search-results-container', container);
            if (control.options.location) {
                createCurrentLocationButton(control);
            }
            if (control.options.id) {
                control.input.id = control.options.id;
            }
            setHandlers(control, container);
            control.input.setAttribute('placeholder', control.options.placeholder);
            return container;
        }

        function clearSearchBox() {
            this.input.focus();
            this.input.value = '';
            this.lastSearchText = '';
            this.clearResultsContainer();
            this.fire(EVENTS.ClearButtonClicked, searchBoxEvent(this.clearButton));
        }

        function setHandlers(control, container) {
            L.DomEvent.disableClickPropagation(container);
            L.DomEvent.disableScrollPropagation(container);
            L.DomEvent.on(container, 'drag', L.DomEvent.stopPropagation);
            L.DomEvent.on(container, 'keydown', createOnKeyDownHandler(control), control);
            L.DomEvent.on(container, 'input', createOnInputChangeHandler(control), control);
            L.DomEvent.on(control._map, 'dragend', onMapEndDrag, control);
            L.DomEvent.on(control.clearButton, 'click', clearSearchBox, control);
            if (control.options.collapsible) {
                L.DomEvent.on(control.input, 'focus', control.expand, control);
                L.DomEvent.on(control.input, 'blur', control.collapse, control);
                if (control.options.collapsed === true) {
                    control.collapse();
                }
            }
        }

        function createResult(control, resultData) {
            var result = L.DomUtil.create('div', 'leaflet-control-search-result', control.resultsContainer);
            result.appendChild(createResultElement(control, resultData));
            control.resultsContainer.style.height = 'auto';
            control.fire(EVENTS.ResultCreated, searchBoxEvent(result, resultData));

            L.DomEvent.on(result, 'click', function() {
                onResultSelect.call(control, resultData);
            }, control);

            L.DomEvent.on(result, 'mouseover', function() {
                this.stopSearch = true;
            }, control);
        }

        function createNoResults(control) {
            var result = L.DomUtil.create('div', 'leaflet-control-search-result', control.resultsContainer);
            result.innerHTML = 'No results found.';
            control.resultsContainer.style.height = 'auto';

            control.fire(EVENTS.ResultCreated);
        }

        function getWordsToHighlight(words) {
            var i;
            words = words.split(/[- ,]+/) || [];

            for (i = 0; i < words.length; i += 1) {
                words[i] = utils.encodeStringForRegex(words[i]);
            }

            return words;
        }

        function highlightWords(control, text) {
            var words = getWordsToHighlight(control.input.value);
            var regex = new RegExp('(' + words.join('|') + ')', 'ig');

            return text.replace(
                regex, '<span class="leaflet-control-search-result-highlight">$1</span>'
            );
        }

        function handleResults(results) {
            var i;
            this.clearResultsContainer();
            this.expand();
            this.fire(EVENTS.ResultsFound, searchBoxEvent(this.resultsContainer, results));
            this._searchResponses = results;

            if (!results || results.length === 0) {
                createNoResults(this);
                return;
            }

            for (i = 0; i < results.length; i += 1) {
                createResult(this, results[i]);
            }

            this.resultsContainer.style.height = 'auto';
            this.fire(EVENTS.ResultsDrawn, searchBoxEvent(this.resultsContainer, results));
        }

        function createResultElement(control, result) {
            var nameWrapper, addressWrapper, address, distanceElement;
            var resultElement = L.DomUtil.create('div', 'geoResponse-result');

            if (typeof result.poi !== 'undefined' && typeof result.poi.name !== 'undefined') {
                nameWrapper = L.DomUtil.create('div', 'geoResponse-result-name');
                nameWrapper.innerHTML = highlightWords(control, result.poi.name);
                resultElement.appendChild(nameWrapper);
            }

            if (typeof result.address !== 'undefined') {
                addressWrapper = L.DomUtil.create('div', 'geoResponse-result-address');
                address = [];

                if (typeof result.address.freeformAddress !== 'undefined') {
                    address.push(result.address.freeformAddress);
                }

                if (typeof result.address.countryCodeISO3 !== 'undefined') {
                    address.push(result.address.countryCodeISO3);
                }

                addressWrapper.innerHTML = highlightWords(control, address.join(', '));
                resultElement.appendChild(addressWrapper);
            }

            if (typeof result.dist !== 'undefined') {
                distanceElement = L.DomUtil.create('div', 'geoResponse-result-distance');
                distanceElement.innerHTML = unitFormatConverter.formatDistance(result.dist);
                resultElement.appendChild(distanceElement);
            }

            return resultElement;
        }

        function onResultSelect(resultData) {
            if (!resultData) {
                return;
            }
            this.stopSearch = true;
            this.input.value = resolveResultText(resultData);
            this.clearResultsContainer();
            this.input.blur();
            this.fire(EVENTS.ResultClicked, searchBoxEvent(this.input, resultData, this.input.value));
        }

        function resolveResultText(resultData) {
            if (!resultData || !resultData.address) {
                return resultData;
            }

            return resultData.poi ? resultData.poi.name + ', ' + resultData.address.freeformAddress : resultData.address.freeformAddress;
        }

        function createOnInputChangeHandler(control) {
            var timerId;

            function promptSearch() {
                if (control.input.value.length === 0 || control.lastSearchText === control.input.value || control.stopSearch) {
                    return;
                }

                control.lastSearchText = control.input.value;

                searchService(getOptionsForSearch(control)).go(function(result) {
                    if (!control.stopSearch) {
                        handleResults.call(control, result);
                    }
                });
            }

            return function() {
                this.fire(EVENTS.SearchInputChanged, searchBoxEvent(this.input, this.input.value, this.input.value));
                control.stopSearch = false;

                if (timerId) {
                    clearTimeout(timerId);
                }

                if (this.options.withPrompt && this.input.value.length >= this.options.minNumberCharsPrompt) {
                    timerId = setTimeout(promptSearch, control.options.idleTimeKeyPress);
                }
            };
        }

        function onMapEndDrag() {
            if (this._searchResponses) {
                this.search(this.input.value);
            }
        }

        function createOnKeyDownHandler(searchBoxControl) {
            var searchOnEnterLastTime = 0,
                searchOnEnterLastValue = '',
                timerId;

            return function(event) {
                var code = event.keyCode || event.which,
                    selectedResult = this.resultsContainer.getElementsByClassName('highlight')[0],
                    selectedResultIndex,
                    currentTime,
                    currentInputValue,
                    throttleTimePrompt,
                    deltaTimeToNextSearch;

                if (code === ENTER && searchBoxControl.input.value) {
                    if (selectedResult) {
                        selectedResultIndex = [].indexOf.call(this.resultsContainer.children, selectedResult);
                        onResultSelect.call(this, this._searchResponses[selectedResultIndex]);
                    } else {
                        if (timerId) {
                            clearTimeout(timerId);
                        }

                        currentTime = new Date().getTime();
                        currentInputValue = searchBoxControl.input.value;
                        throttleTimePrompt = searchBoxControl.options.throttleTimePrompt || 1000;
                        deltaTimeToNextSearch = throttleTimePrompt - (currentTime - searchOnEnterLastTime);

                        if (deltaTimeToNextSearch <= 0 || searchOnEnterLastValue !== currentInputValue) {
                            searchOnEnterLastTime = currentTime;
                            searchOnEnterLastValue = currentInputValue;
                            searchBoxControl.search(currentInputValue);
                        } else {
                            timerId = setTimeout(function() {
                                searchOnEnterLastTime = new Date().getTime();
                                searchOnEnterLastValue = searchBoxControl.input.value;
                                searchBoxControl.search(searchBoxControl.input.value);
                            }, deltaTimeToNextSearch);
                        }
                    }
                }

                if (this.resultsContainer.hasChildNodes()) {
                    if (code === ARROWUP || code === ARROWDOWN) {
                        this.stopSearch = true;
                        highlightNextResult.call(this, code, selectedResult, this.resultsContainer);
                    }
                }
            };
        }

        function highlightNextResult(keyCode, selectedElement, resultsContainerElement) {
            var highlightClass = 'highlight',
                rules = {
                    38: {
                        edgeCase: 'firstChild',
                        nextSibling: 'previousElementSibling',
                        nextAfterEdgeCase: 'lastChild'
                    },
                    40: {
                        edgeCase: 'lastChild',
                        nextSibling: 'nextElementSibling',
                        nextAfterEdgeCase: 'firstChild'
                    }
                },
                newlySelectedElement;
            if (selectedElement) {
                selectedElement.className = selectedElement.className.replace(highlightClass, '');
            }

            if (!selectedElement || selectedElement === resultsContainerElement[rules[keyCode].edgeCase]) {
                newlySelectedElement = resultsContainerElement[rules[keyCode].nextAfterEdgeCase];
            } else {
                newlySelectedElement = selectedElement[rules[keyCode].nextSibling];
            }

            newlySelectedElement.className += ' ' + highlightClass;
        }

        function getOptionsForSearch(control) {
            var options = utils.clone(control.options.serviceOptions);
            options.query = control.input.value;
            if (control.options.viewportBoundingBox) {
                options.boundingBox = control._map.getBounds();
            } else {
                options.center = control._map.getCenter();
            }
            if (control.options.disablePOIs) {
                options.idxSet = 'PAD,Str,Xstr,Geo,Addr';
            }
            if (typeof options.typeahead !== 'boolean') {
                options.typeahead = true;
            }

            if (typeof options.language !== 'string') {
                options.language = control.options.language;
            }

            return options;
        }

        SearchBox = L.Control.extend({
            includes: L.Mixin.Events,

            options: {
                placeholder: 'Search for place...',
                id: false,
                collapsible: true,
                collapsed: false,
                location: false,
                icon: 'icon-search',
                disablePOIs: false,
                viewportBoundingBox: false,
                minNumberCharsPrompt: 3,
                throttleTimePrompt: 1000,
                idleTimeKeyPress: 200,
                serviceOptions: {},
                position: 'topleft',
                withPrompt: true
            },
            initialize: function(options) {
                L.Util.setOptions(this, options);
            },
            onAdd: function() {
                return createDOMElements(this);
            },
            onRemove: function() {
                this.clearAllEventListeners();
            },
            /**
             * Method used to clear container of search call results.
             *
             * @method clearResultsContainer
             */
            clearResultsContainer: function() {
                if (this.resultsContainer.hasChildNodes()) {
                    this.resultsContainer.style.height = '0px';
                    this.resultsContainer.innerHTML = '';
                    this._searchResponses = undefined;
                    this.fire(EVENTS.ResultsCleared, searchBoxEvent(this.resultsContainer));
                }
            },
            /**
             * Method used to set data into searchBox without calling a service.
             *
             * @method setResultData
             * @param {Object} resultData Result data given from search service. Must contain address and/or POI
             *     information.
             *
             *  @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             *
             * var resultData = {
             *                    address: {freeformAddress: "Amsterdam"},
             *                    poi: { name: "TomTom, Oosterdoksstraat, Amsterdam, The Netherlands" }
             *                  }
             * searchBoxInstance.setResultData(resultData);
             * ```
             */
            setResultData: function(resultData) {
                if (resultData) {
                    this.input.value = resolveResultText(resultData);
                    this.clearResultsContainer();
                    this.input.blur();
                }
            },
            /**
             * Method used to call a search service with given data.
             *
             * @method search
             * @param {Object} text String object or reverse geocoding object (with query parameter) as a search
             *     query.
             *
             *  @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.search("Amsterdam");
             *
             * var reversedGeocodeResponse = {query: "Amsterdam"}
             * searchBoxInstance.search(reversedGeocodeResponse);
             *
             * ```
             */
            search: function(text) {
                var searchBox = this;
                text = text.query || text;
                if (typeof text !== 'string' || text.length === 0) {
                    return;
                }
                this.input.focus();
                this.input.value = text;
                this.lastSearchText = text;

                searchService(getOptionsForSearch(searchBox)).go(function(results) {
                    handleResults.call(searchBox, results);
                });
            },
            /**
             * Method used to collapse search. Collapsing is possible only if search is empty.
             *
             * @method collapse
             */
            collapse: function() {
                if (!this.input.value) {
                    this._container.className += ' collapsed';
                    this.clearResultsContainer();
                } else {
                    this.expand();
                }
            },
            /**
             * Method used to expand collapsed search.
             *
             * @method expand
             */
            expand: function() {
                this._container.className = this._container.className.replace(/\b collapsed\b/, '');
            },
            /**
             * Method used to get main container of a searchBox.
             *
             * @method getContainer
             *
             * @return {Node} HTML element representing container of a searchBox.
             */
            getContainer: function() {
                return this._container;
            },
            /**
             * Method used to get location element (if set and added).
             *
             * @method getLocation
             *
             * @return {Node} HTML element representing location element.
             */
            getLocation: function() {
                return this.location;
            },
            /**
             * Method used to set icon by styling search box input.
             *
             * @method setIcon
             * @param className Name of the CSS class that sets _background-image_ property for search box input.
             */
            setIcon: function(className) {
                if (typeof className === 'string') {
                    this.input.className = this.input.className.replace(/(icon)((-)(\w+))*/, className);
                }
            },
            /**
             * Method used to add location icon.
             *
             * @method addLocationIcon
             */
            addLocationIcon: function() {
                if (this.location === undefined) {
                    this.options.location = true;
                    createCurrentLocationButton(this);
                }
            },
            /**
             * Method used to remove location icon.
             *
             * @method removeLocationIcon
             */
            removeLocationIcon: function() {
                if (this.location !== undefined) {
                    this.options.location = false;
                    this.locationIcon.parentNode.removeChild(this.locationIcon);
                    this.location.clearAllEventListeners();
                    this.location = undefined;
                }
            },

            /**
             * A setter for _'language'_ option.
             * Whitelist is defined on [supported language codes list](https://developer.tomtom.com/online-search/online-search-documentation/supported-languages)
             *
             * @method language
             * @param {String} [language] Language code, for example 'en-GB', 'en-US', 'de-DE'.
             *        Full list available on (https://developer.tomtom.com/online-search/online-search-documentation/supported-languages)
             */
            language: function(languageCode) {
                if (null === languageCode || '' === languageCode) {
                    delete this.options.language;
                } else {
                    this.options.language = languageCode;
                }
            },

            /**
             * Whenever the input within the search box is changed by the user this event is triggered.
             *
             * @event SearchInputChanged
             * @param {Object} data The current text entered by the user.
             * @param {String} text Same as data.
             * @param {String} type Event's name, equal to "searchInputChanged".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.SearchInputChanged, function (eventObject) {
             *      console.log(eventObject.text);
             * });
             * ```
             */
            /**
             * This event appears when response from search service is being processed. For each result which is being
             * rendered on the prompt list such event is fired.
             *
             * @event ResultCreated
             * @param {Object} data The result from search service response that is being processed.
             * @param {String} type Event's name, equal to "resultCreated".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultCreated, function (eventObject) {
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when user selects the result from list or when current location is identified.
             *
             * @event ResultClicked
             * @param {Object} data Selected result object from search service response.
             * @param {String} text The human readable string representation of selected result.
             * @param {String} type Event's name, equal to "resultClicked".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultClicked, function (eventObject) {
             *      console.log(eventObject.text);
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when the response from search service is already processed and rendered in a form
             * of list of possible results.
             *
             * @event ResultsDrawn
             * @param {Array} data Array of search service results. Each result has, among other parameters, its
             *     _type_, _address_ and _position_.
             * @param {String} type Event's name, equal to "resultsDrawn".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultsDrawn, function (eventObject) {
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when the response from search service appears for both normal and prompt query.
             *
             * @event ResultsFound
             * @param {Array} data Array of search service results. Each result has, among other parameters, its
             *     _type_, _address_ and _position_.
             * @param {String} type Event's name, equal to "resultsFound".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultsFound, function (eventObject) {
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when user clicks the clear search box input button.
             *
             * @event ClearButtonClicked
             * @param {String} type Event's name, equal to "clearButtonClicked".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = tomtom.searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ClearButtonClicked, function () {
             *      console.log("User clicked the clear button");
             * });
             * ```
             */
            /**
             * Event that is fired when prompt results list is being cleared.
             *
             * @event ResultsCleared
             * @param {String} type Event's name, equal to "resultsCleared".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = tomtom.searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.LocationsCleared, function () {
             *      console.log("Results were cleared");
             * });
             * ```
             */
            /**
             * Event that is fired when obtaining user's current location fails.
             *
             * @event LocationError
             * @param {Object} eventObject All failure information.
             * @param {String} type Event's name, equal to "locationError".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = tomtom.searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.LocationError, function (eventObject) {
             *      console.log(eventObject);
             * });
             * ```
             */
            Event: EVENTS
        });

        /**
         * The method to instantiates the searchBox control.
         *
         * @method searchBox
         * @param {Object} [options] Options to be passed to the searchBox control.
         * @param {String} [options.placeholder="Search for place..."] The placeholder text to be displayed inside
         *     searchBox.
         * @param {String} [options.id] Id of a searchBox's DOM element.
         * @param {Boolean} [options.collapsed=false] Flag if checkbox is collapsed on initialization.
         * @param {Boolean} [options.collapsible=true] Flag if searchBox can be collapsed.
         * @param {Boolean} [options.location=false] Display location button to search for current location.
         * @param {String} [options.icon="icon-search"] Determines icon of the searchBox.
         * @param {Boolean} [options.disablePOIs=false] Determines whether search results should not contain POIs.
         * @param {Boolean} [options.viewportBoundingBox=false] Enables or disables searching on a currently visible
         *     map area. If unset, search service will use current map center.
         * @param {Boolean} [options.withPrompt=true] Should widget show user prompts while typing, the call will be
         *     throttled according to the following settings: minNumberCharsPrompt,throttleTimePrompt,idleTimeKeyPress
         * @param {Integer} [options.minNumberCharsPrompt=3] Sets the minimal required number of characters that are
         *     required to perform search.
         * @param {Integer} [options.throttleTimePrompt=1000] Sets the minimal time between the following search
         *     service calls when user types text into the input.
         * @param {Integer} [options.idleTimeKeyPress=200] Sets the time between following key press events to assume
         *     the user finished typing the query.
         * @param {Function} [options.language] Language code, for example 'en-GB', 'en-US', 'de-DE'. Full list
         *     available on (https://developer.tomtom.com/online-search/online-search-documentation/supported-languages)
         * @param {Object} [options.serviceOptions] Options to be passed to service. See search service options.
         *
         * @example
         * ```javascript
         *
         *  // SearchBox with location button and Polish language
         *  var options = {
         *      location: true,
         *      language: "pl-PL"
         *  };
         *
         * var searchBoxInstance = searchBox(options).addTo(map);
         *
         * // some time later...
         * searchBoxInstance.language('de-DE');
         *
         * ```
         */
        return function(options) {
            return new SearchBox(options);
        };
    });

